import React, { useCallback, useMemo, useState } from 'react'
import { FormattedResultContentType, ResultDisplayProps } from '../../common'
import { ConversationTimelineResult, useConversationTimelineResult } from './hooks'
import classNames from 'classnames'
import './ConversationTimeline.scss'
import { Button } from '../../../../../../../components/Button/Button'
import { DefaultResultDisplay } from '../../DefaultDisplay/DefaultResultDisplay'
import { AIResultStatus } from '@zocks-communications/client-sdk-js'
import { NoMeetingResult } from '../../DefaultDisplay/View/NoMeetingResult'
import { LEXICAL_NODES } from '../../Lexical/nodes'
import FloatingToolbarPlugin from '../../Lexical/Plugins/FloatingToolbarPlugin/FloatingToolbarPlugin'
import { DefaultFloatingToolbarContent } from '../../Lexical/Plugins/FloatingToolbarPlugin/DefaultFloatingToolbarContent/DefaultFloatingToolbarContent'
import { CommentPlugin } from '../../Lexical/Plugins/CommentPlugin/CommentPlugin'
import { ReadOnlyLexicalEditor } from '../../Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { useEditingAiResult } from '../../DefaultDisplay/hooks'
import { EditorState } from 'lexical'
import { AiResult, StructuredSummaryResult, StructuredSummaryResultType } from '../../../../../../../models/AiResult'
import { getResultDisplayValues } from '../../DefaultDisplay/utils'
import { NotepadPlugin } from '../../Lexical/Plugins/NotepadPlugin/NotepadPlugin'
import { AiFeature } from '../../../../../../../models/AiFeature'
import { CopyContent, CopyData, getEmptyResultCopyContent, HTML_TYPE, TEXT_TYPE } from '../../../copy'
import { isArray, isObject } from '../../../../../../../common/util'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectRoomSession } from '../../../../../../../store/meetingSummary/roomSession'
import { parseMetadata } from '../../../utils'
import { ParticipantSentiment, SentimentAnalysisMetadata } from '../../../SentimentAnalysis/model'

import { SentimentDistributionInfo } from '../../../SentimentAnalysis/SentimentDistribution'
import { SentimentTrendChart } from '../../../SentimentAnalysis/SentimentTrendChart'
import { filterCloseTimelineItems, getSentimentByTopic } from './sentiment_helper'
import { useAccount } from '../../../../../../../common/useAccount'

export const copyConversationTimeline = (aiFeature: AiFeature, aiResult: AiResult, copyData?: CopyData): CopyContent => {
  if (!aiResult.result) {
    return getEmptyResultCopyContent()
  }

  const structuredSummaryResult = aiResult.result as StructuredSummaryResult
  if (!isArray(structuredSummaryResult.result)) {
    return {
      [TEXT_TYPE]: structuredSummaryResult.result as string,
      [HTML_TYPE]: structuredSummaryResult.result as string,
    }
  }

  const copiedResults = (structuredSummaryResult.result as StructuredSummaryResultType[])
    .map((result) => {
      if (isObject(result)) {
        const objectResult = result as { [key: string]: StructuredSummaryResultType | StructuredSummaryResultType[] }
        const topic = objectResult.Topic || objectResult.topic
        const percentage = objectResult.Percentage || objectResult.percentage
        if (topic && percentage) {
          return {
            [TEXT_TYPE]: `${topic} - ${percentage}`,
            [HTML_TYPE]: `${topic} - <b>${percentage}</b>`,
          } as CopyContent
        }
      }
      return null
    })
    .filter((result): result is CopyContent => !!result)

  return {
    [TEXT_TYPE]: copiedResults.map((c) => c[TEXT_TYPE]).join('\n'),
    [HTML_TYPE]: copiedResults.map((c) => c[HTML_TYPE]).join('<br/>'),
  }
}

export const ConversationTimeline: React.FC<ResultDisplayProps> = ({ aiFeature, aiResult: displayedAiResult, row, collapsed, setCollapsed }) => {
  const { account } = useAccount({ refetchOnMountOrArgChange: false })
  const { displayMode, results } = useConversationTimelineResult(displayedAiResult)
  const [hoveredResult, setHoveredResult] = useState<ConversationTimelineResult | null>(null)
  const { aiResult, handleReadOnlyFormattedResultChanged } = useEditingAiResult(aiFeature, displayedAiResult)
  const roomSession = useAppSelector(selectRoomSession)

  const sentimentAnalysis = useMemo(() => {
    if (!roomSession) {
      return null
    }

    const metadata = parseMetadata(roomSession.metadata) as SentimentAnalysisMetadata
    const participants = metadata?.sentimentAnalysis?.participants ?? null
    if (participants) {
      const timelineItems = (Object.values(participants) as ParticipantSentiment[])
        .flatMap((participant: ParticipantSentiment) => participant.timeline)

        .sort((a, b) => a.timestamp - b.timestamp)

      return filterCloseTimelineItems(timelineItems)
    }

    return null
  }, [roomSession])

  if (displayMode === 'default') {
    return <DefaultResultDisplay aiFeature={aiFeature} aiResult={aiResult} row={row} collapsed={collapsed} setCollapsed={setCollapsed} />
  }

  if (aiResult.status === AIResultStatus.NO_RESULT || !results.length) {
    return (
      <div className='d-flex flex-column gap-3'>
        <NoMeetingResult />
        <div className='d-flex'>
          <Button variant='darkGhost' className='flex-grow-1 button-small' onClick={() => setCollapsed(!collapsed)}>
            Show less
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      {account?.effectiveConfig.sentimentAnalysis && sentimentAnalysis && (
        <div className='mb-2 sentiment-analysis-chart'>
          <SentimentTrendChart timeline={sentimentAnalysis} width='100%' height='300px' />
        </div>
      )}
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex gap-4'>
          <div className='d-flex flex-column gap-3 conversation-timeline-collapsed-info-container w-100'>
            {results.map((result, idx) => {
              return (
                <div className='py-1 px-2' key={`result${idx}`}>
                  <div className='conversation-timeline-opened-percentage' style={{ width: result.percentage }}></div>
                  <ConversationTimelineResultView
                    key={result.topic}
                    aiFeature={aiFeature}
                    aiResult={aiResult}
                    result={result}
                    hoveredResult={hoveredResult}
                    setHoveredResult={setHoveredResult}
                    path={`$.result[${idx}]`}
                    handleReadOnlyFormattedResultChanged={handleReadOnlyFormattedResultChanged}
                  />
                </div>
              )
            })}
          </div>
        </div>
        <div className='d-flex'>
          <Button variant='darkGhost' className='flex-grow-1 button-small' onClick={() => setCollapsed(!collapsed)}>
            Show less
          </Button>
        </div>
      </div>
    </>
  )
}

interface ConversationTimelineResultViewProps {
  aiFeature: AiFeature
  aiResult: AiResult
  result: ConversationTimelineResult
  hoveredResult: ConversationTimelineResult | null
  setHoveredResult: (result: ConversationTimelineResult | null) => void
  handleReadOnlyFormattedResultChanged: (basePath: string, path: string, contentType: string, content: string) => void
  path: string
}

const ConversationTimelineResultView: React.FC<ConversationTimelineResultViewProps> = ({
  aiFeature,
  aiResult,
  result,
  hoveredResult,
  setHoveredResult,
  handleReadOnlyFormattedResultChanged,
  path,
}) => {
  const roomSession = useAppSelector(selectRoomSession)
  const { account } = useAccount({ refetchOnMountOrArgChange: false })
  const sentiment = useMemo(() => {
    if (!roomSession) {
      return null
    }
    const metadata = parseMetadata(roomSession.metadata) as SentimentAnalysisMetadata
    const participants = metadata?.sentimentAnalysis?.participants ?? null
    if (!participants) {
      return null
    }

    return (
      getSentimentByTopic(
        participants,
        //@ts-ignore
        (aiResult.result as StructuredSummaryResult)?.result.map((r) => {
          return { topic: r.Topic, percentage: r.Percentage }
        }) ?? [],
        new Date(roomSession.startedAt).getTime() / 1000,
        new Date(roomSession.finishedAt!).getTime() / 1000,
      )[result.topic] ?? []
    )
  }, [roomSession, aiResult.result])

  const handleChange = useCallback(
    (editorState: EditorState) => {
      handleReadOnlyFormattedResultChanged('$', path, FormattedResultContentType.LEXICAL, JSON.stringify(editorState.toJSON()))
    },
    [handleReadOnlyFormattedResultChanged, path],
  )

  const htmlValue = (result: ConversationTimelineResult) => {
    return `<p><b>${result.percentage}</b> ${result.topic}</p>`
  }
  const { htmlResult, lexicalResult } = useMemo(() => {
    return getResultDisplayValues(aiResult.result as StructuredSummaryResult, path)
  }, [aiResult, path])

  return (
    <div
      className={classNames('d-flex flex-column gap-2', { 'zocks-text-primary': hoveredResult?.topic === result.topic })}
      onMouseEnter={() => setHoveredResult(result)}
      onMouseLeave={() => setHoveredResult(null)}
    >
      <ReadOnlyLexicalEditor
        initialHtmlValue={htmlResult ?? htmlValue(result)}
        initialEditorState={lexicalResult}
        onChange={handleChange}
        lexicalNodes={LEXICAL_NODES}
      >
        <FloatingToolbarPlugin>
          <DefaultFloatingToolbarContent withoutResultExplainer aiFeature={aiFeature} aiResult={aiResult} />
        </FloatingToolbarPlugin>
        <CommentPlugin aiResult={aiResult} path={path} />
        <NotepadPlugin />
      </ReadOnlyLexicalEditor>
      <div className='d-flex flex-row gap-4'>
        {account?.effectiveConfig.sentimentAnalysis && sentiment && (
          <SentimentDistributionInfo distribution={sentiment.distribution} useEmojis={true} ignoreZero={true} />
        )}
      </div>
    </div>
  )
}
